import { InAppState } from "../base/InAppState";
import template from "./SettingsTemplate.html";
import { _lcs } from "../../i18n";
import { validateScope, resetValidation, bindLiveClear, markInvalid } from "../../components/general/form-validation";

/**
 * SettingsState — "Einstellungen" (account menu): the operator's own account
 * settings — for now the password (PATCH /auth/user/password, which checks the
 * current one). The same screen as the client-admin's; the interface language
 * lives in the account menu itself.
 */
export class SettingsState extends InAppState
{
    // The API's own minimum (UserPatchPasswordRequest).
    static MIN_PASSWORD = 6;

    static ROUTE = '/settings';
    static ID = 'settings';

    async enter()
    {
        this.saving = false;

        this.app.view.render( this.app.view.pageContent, template, {
            "email" : this.app.user?.email || "",
            "minLength" : SettingsState.MIN_PASSWORD
        } );

        this.form = this.app.view.pageContent.querySelector( '[data-id="password-form"]' );
        bindLiveClear( this.form );

        this.addEventListener( this.form, "submit", ( e ) => this.onSubmit( e ) );
        this.addEventListener( this.form, "click", ( e ) => this.onToggle( e ) );
    }

    /** Show / hide one password field (the eye button inside it). */
    onToggle( e )
    {
        const toggle = e.target.closest( "[data-toggle]" );
        if ( !toggle )
        {
            return;
        }
        const input = this.form.querySelector( "#" + toggle.getAttribute( "data-toggle" ) );
        const show = input.type === "password";
        input.type = show ? "text" : "password";
        toggle.setAttribute( "aria-pressed", String( show ) );
        toggle.setAttribute( "aria-label", show ? _lcs( "Passwort verbergen" ) : _lcs( "Passwort anzeigen" ) );
        const icon = toggle.querySelector( ".icon" );
        icon.classList.toggle( "icon--eye", !show );
        icon.classList.toggle( "icon--eye-off", show );
        input.focus();
    }

    async onSubmit( e )
    {
        e.preventDefault();
        if ( this.saving )
        {
            return;
        }

        const current = this.form.querySelector( "#pw-current" );
        const next = this.form.querySelector( "#pw-new" );
        const repeat = this.form.querySelector( "#pw-repeat" );

        // Rules the markup cannot express, as custom validity — validateScope
        // then shows them like any other field error.
        next.setCustomValidity( next.value && next.value === current.value
            ? _lcs( "Das neue Passwort muss sich vom aktuellen unterscheiden." ) : "" );
        repeat.setCustomValidity( repeat.value && repeat.value !== next.value
            ? _lcs( "Die Passwörter stimmen nicht überein." ) : "" );

        if ( !validateScope( this.form ) )
        {
            return;
        }

        this.setSaving( true );
        try
        {
            const { result } = await this.app.api.patch( "/auth/user/password", {
                "currentPassword" : current.value,
                "newPassword" : next.value
            } );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected password response." );
            }

            if ( this.isActive() )
            {
                this.form.reset();
                resetValidation( this.form );
            }
            this.app.view.toast( _lcs( "Dein Passwort wurde geändert." ), "success" );
        }
        catch ( err )
        {
            console.error( err );
            if ( !this.isActive() )
            {
                return;
            }
            if ( +err?.status === 403 )
            {
                markInvalid( current, _lcs( "Das aktuelle Passwort stimmt nicht." ) );
                current.focus();
            }
            else
            {
                this.app.view.toast( _lcs( "Das Passwort konnte nicht geändert werden. Bitte versuche es später erneut." ), "error" );
            }
        }
        finally
        {
            if ( this.isActive() )
            {
                this.setSaving( false );
            }
        }
    }

    setSaving( saving )
    {
        this.saving = saving;
        const button = this.form.querySelector( '[type="submit"]' );
        button.disabled = saving;
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Einstellungen" ),
            subtitle: _lcs( "Dein Konto" )
        };
    }
}
